import i18n, { SupportedLocale } from "@/i18n";
import {
  computeFieldDate,
  computeFieldMultipleprintDimensionImage,
  computeFieldMultipleprintDimensions,
  computeFieldMultipleprintRelatedAuthors,
} from "@/lib/computeFields";
import { AdvancedSearchConfig, attachmentPhotoAdvancedSearchGroup } from "../forms/advanced-search/advanced-search-fields-def";
import { artistLastNameSortGroup } from "../forms/advanced-search/artist-sort-fields";
import { ColumnFormatted } from "./general";
import { multipleprintNatureLabel } from "@/lib/multipleprint-nature";
import { MULTIPLEPRINT_NATURES } from "@/lib/types";

export const multipleprintsColumns: ColumnFormatted[] = [
  {
    type: "string",
    key: "estampesMultiplesDescriptionNumeroEnregistrement",
    visible: true,
  },
  {
    type: "string",
    key: "estampesMultiplesDescriptionNumeroInventaire",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesDescriptionNumeroAncien",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesDescriptionNumeroAutres",
    visible: false,
  },
  { type: "image", key: "vignette", visible: true },
  {
    type: "computed",
    key: "estampesDescriptionNomArtisteComplet",
    visible: true,
    formattedData(data) {
      return computeFieldMultipleprintRelatedAuthors(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionAuteurArtisteAttribueA",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionAuteurArtisteAnonyme",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionAuteurArtisteDApres",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionAuteurArtisteEntourageDe",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionAuteurAtelierDe",
    visible: false,
  },
  { type: "string", key: "estampesMultiplesDescriptionTitre", visible: true },
  {
    type: "string",
    key: "estampesMultiplesDescriptionAutreTitre",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesDescriptionSousTitre",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionTitreSansTitre",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionTitreInconnu",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionTitreIncertin",
    visible: false,
  },
  {
    type: "computed",
    key: "estampesMultiplesDescriptionDateDeCreation",
    visible: false,
    formattedData(data) {
      return computeFieldDate(
        {
          date: data.estampesMultiplesDescriptionDateDeCreation,
          unknownDate: data.estampesMultiplesDescriptionDateDeCreationInconnue,
          uncertainDate:
            data.estampesMultiplesDescriptionDateDeCreationIncertaine,
        },
        { language: i18n.language as SupportedLocale },
      );
    },
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDateDeCreationInconnue",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDateDeCreationIncertaine",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDateDeCreationEstimee",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionNumerisationAFaire",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionPhotoWebReady",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionPhotoHdReady",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesPhotosCreditOeuvres",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesPhotosCreditPhotoOeuvres",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesPhotosCreditRemarques",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesDescriptionMotsCles",
    visible: false,
  },
  {
    type: "multilingual",
    key: "estampesMultiplesDescriptionTypeMultilingual",
    visible: true,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionOeuvreDoubleFace",
    visible: false,
  },
  {
    type: "multilingual",
    key: "estampesMultiplesDescriptionTechnique",
    visible: true,
  },
  {
    type: "multilingual",
    key: "estampesMultiplesDescriptionSupportMateriau",
    visible: true,
  },
  {
    type: "string",
    key: "estampesMultiplesDescriptionEditeur",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesDescriptionFabricantImprimeur",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDimensionsProfondeur",
    visible: false,
  },
  {
    type: "computed",
    key: "estampesMultiplesDescriptionDimensionsOeuvre",
    visible: true,
    formattedData(data) {
      return computeFieldMultipleprintDimensions(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDimensionsOeuvreVerifiees",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDimensionsOeuvreInconnues",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDimensionsOeuvreDouteuses",
    visible: false,
  },
  {
    type: "computed",
    key: "estampesMultiplesDescriptionDimensionsImage",
    visible: false,
    formattedData(data) {
      return computeFieldMultipleprintDimensionImage(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDimensionsImageVerifiees",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDimensionsImageInconnues",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesDescriptionDimensionsImageDouteuses",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesDescriptionPoidsOeuvre",
    visible: false,
  },
  {
    type: "computed",
    key: "estampesMultiplesDescriptionDescriptionCompleteMultilingual",
    visible: false,
    formattedData(data) {
      if (
        Object.keys(
          data.estampesMultiplesDescriptionDescriptionCompleteMultilingual ??
            {},
        ).length === 0
      )
        return "";
      return (
        data.estampesMultiplesDescriptionDescriptionCompleteMultilingual?.[
          i18n.language as SupportedLocale
        ] ?? ""
      );
    },
  },
  {
    type: "richtext",
    key: "estampesMultiplesDescriptionResume",
    visible: false,
  },
  {
    type: "multilingual",
    key: "estampesMultiplesDescriptionSignatureLocalisation",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesDescriptionSignatureTranscription",
    visible: false,
  },
  {
    type: "multilingual",
    key: "estampesMultiplesDescriptionInscriptionsDos",
    visible: false,
  },
  {
    type: "richtext",
    key: "estampesMultiplesDescriptionNotesTravail",
    visible: false,
  },
  {
    type: "computed",
    key: "estampesMultiplesDescriptionNotesPubliquesMultilingual",
    visible: false,
    formattedData(data) {
      if (
        Object.keys(
          data.estampesMultiplesDescriptionNotesPubliquesMultilingual ?? {},
        ).length === 0
      )
        return "";
      return (
        data.estampesMultiplesDescriptionNotesPubliquesMultilingual?.[
          i18n.language as SupportedLocale
        ] ?? ""
      );
    },
  },
  {
    type: "string",
    key: "estampesMultiplesMatriceType",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesMatriceNumeroEnregistrement",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesMatriceNumeroInventaire",
    visible: false,
  },
  {
    type: "dimensions",
    key: "estampesMultiplesMatriceDimensions",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesMatriceDimensionsRaw",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesMatriceWeight",
    visible: false,
  },
  {
    type: "string",
    key: "estampesMultiplesMatriceRangement",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesMatriceNotLocated",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesMatriceDestroy",
    visible: false,
  },
  {
    type: "boolean",
    key: "estampesMultiplesMatriceNotPrintable",
    visible: false,
  },
  {
    type: "richtext",
    key: "estampesMultiplesMatriceStateNotes",
    visible: false,
  },
  {
    type: "richtext",
    key: "estampesMultiplesMatriceNotes",
    visible: false,
  },
  { type: "status", key: "estampesMultiplesPublicationStatus", visible: true },
  {
    type: "badge",
    key: "estampesMultiplesNature",
    visible: true,
    // Badge dédié pour chaque nature : « Multiple simple » comme « Portfolio ».
    formattedData(data) {
      return multipleprintNatureLabel(
        i18n.t,
        data.estampesMultiplesNature ?? "simple",
      );
    },
  },
  { type: "richtext", key: "sources", visible: false },
];

export const multipleprintsColumnsVisibility = multipleprintsColumns.reduce(
  (acc, column) => ({
    ...acc,
    [column.key]: column.visible ?? true,
  }),
  {} as Record<string, boolean>,
);

/** Visibilité par défaut pour les tableaux "multiples" dans les enregistrements liés (colonnes utilisées par MultipleLinks, etc.). */
export const multipleprintsLinksColumnsVisibility: Record<string, boolean> = {
  estampesMultiplesDescriptionNumeroEnregistrement: true,
  estampesMultiplesDescriptionVignette: true,
  estampesMultiplesDescriptionTitre: true,
  author: true,
  estampesMultiplesDescriptionDateDeCreation: true,
  actions: true,
};

const multipleprintBibliographyAdvancedSearchLabels = {
  reproduitPage: "multipleprintBibliography.advancedSearch.reproduitPage",
  reproduitPlanche: "multipleprintBibliography.advancedSearch.reproduitPlanche",
  plancheNonNumerotee:
    "multipleprintBibliography.advancedSearch.plancheNonNumerotee",
  citePage: "multipleprintBibliography.advancedSearch.citePage",
  listePage: "multipleprintBibliography.advancedSearch.listePage",
  sousNumero: "multipleprintBibliography.advancedSearch.sousNumero",
  reproductionCouleur:
    "multipleprintBibliography.advancedSearch.reproductionCouleur",
  reproductionCouverture:
    "multipleprintBibliography.advancedSearch.reproductionCouverture",
  nonPagine: "multipleprintBibliography.advancedSearch.nonPagine",
  texteLibre: "multipleprintBibliography.advancedSearch.texteLibre",
} as const;

const multiplesampleBibliographyAdvancedSearchLabels = {
  reproduitPage: "multiplesampleBibliography.advancedSearch.reproduitPage",
  reproduitPlanche: "multiplesampleBibliography.advancedSearch.reproduitPlanche",
  plancheNonNumerotee:
    "multiplesampleBibliography.advancedSearch.plancheNonNumerotee",
  citePage: "multiplesampleBibliography.advancedSearch.citePage",
  listePage: "multiplesampleBibliography.advancedSearch.listePage",
  sousNumero: "multiplesampleBibliography.advancedSearch.sousNumero",
  reproductionCouleur:
    "multiplesampleBibliography.advancedSearch.reproductionCouleur",
  reproductionCouverture:
    "multiplesampleBibliography.advancedSearch.reproductionCouverture",
  nonPagine: "multiplesampleBibliography.advancedSearch.nonPagine",
  texteLibre: "multiplesampleBibliography.advancedSearch.texteLibre",
} as const;

export const multipleprintsAdvancedSearchConfig: AdvancedSearchConfig = [
  {
    type: "group",
    label: "multipleprintsDescription",
    fields: [
      {
        type: "string",
        key: "estampesMultiplesDescriptionNumeroEnregistrement",
      },
      {
        type: "string",
        key: "estampesMultiplesDescriptionNumeroInventaire",
      },
      {
        type: "string",
        key: "estampesMultiplesDescriptionNumeroAncien",
      },
      {
        type: "string",
        key: "estampesMultiplesDescriptionNumeroAutres",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionNumeroInventairePublished",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionNumeroAncienPublished",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionNumeroAutresPublished",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionAuteurArtisteAttribueA",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionAuteurArtisteAnonyme",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionAuteurArtisteDApres",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionAuteurArtisteEntourageDe",
      },
      { type: "boolean", key: "estampesMultiplesDescriptionAuteurAtelierDe" },
      { type: "string", key: "estampesMultiplesDescriptionTitre" },
      { type: "string", key: "estampesMultiplesDescriptionAutreTitre" },
      { type: "string", key: "estampesMultiplesDescriptionSousTitre" },
      { type: "boolean", key: "estampesMultiplesDescriptionTitreSansTitre" },
      { type: "boolean", key: "estampesMultiplesDescriptionTitreInconnu" },
      { type: "boolean", key: "estampesMultiplesDescriptionTitreIncertin" },
      {
        type: "multilingual",
        key: "estampesMultiplesDescriptionTypeMultilingual",
      },
      {
        type: "enum",
        key: "estampesMultiplesNature",
        // Getters : la config est figée au chargement du module, la langue non.
        options: MULTIPLEPRINT_NATURES.map((nature) => ({
          name: nature,
          get label() {
            return multipleprintNatureLabel(i18n.t, nature);
          },
        })),
      },
      { type: "boolean", key: "estampesMultiplesDescriptionOeuvreDoubleFace" },
      {
        type: "string",
        key: "estampesMultiplesDescriptionDateDeCreation",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionDateDeCreationInconnue",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionDateDeCreationIncertaine",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionDateDeCreationEstimee",
      },
      { type: "string", key: "estampesMultiplesDescriptionMotsCles" },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionNumerisationAFaire",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionPhotoWebReady",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionPhotoHdReady",
      },
      {
        type: "string",
        key: "estampesMultiplesPhotosCreditOeuvres",
      },
      {
        type: "string",
        key: "estampesMultiplesPhotosCreditPhotoOeuvres",
      },
      {
        type: "string",
        key: "estampesMultiplesPhotosCreditRemarques",
      },
      { type: "multilingual", key: "estampesMultiplesDescriptionTechnique" },
      {
        type: "multilingual",
        key: "estampesMultiplesDescriptionSupportMateriau",
      },
      { type: "string", key: "estampesMultiplesDescriptionEditeur" },
      { type: "string", key: "estampesMultiplesDescriptionFabricantImprimeur" },
      { type: "string", key: "estampesMultiplesDescriptionResume" },
      {
        type: "multilingual",
        key: "estampesMultiplesDescriptionSignatureLocalisation",
      },
      {
        type: "string",
        key: "estampesMultiplesDescriptionSignatureTranscription",
      },
      {
        type: "multilingual",
        key: "estampesMultiplesDescriptionInscriptionsDos",
      },
      { type: "string", key: "estampesMultiplesDescriptionNotesTravail" },
      {
        type: "multilingual",
        key: "estampesMultiplesDescriptionNotesPubliquesMultilingual",
      },
      {
        type: "string",
        key: "estampesMultiplesDescriptionDimensionsOeuvreRaw",
      },
    ],
  },
  {
    type: "group",
    label: "estampesMultiplesMatrice",
    fields: [
      { type: "string", key: "estampesMultiplesMatriceType" },
      { type: "string", key: "estampesMultiplesMatriceNumeroEnregistrement" },
      { type: "string", key: "estampesMultiplesMatriceNumeroInventaire" },
      {
        type: "dimensions",
        key: "estampesMultiplesMatriceDimensions",
      },
      { type: "string", key: "estampesMultiplesMatriceDimensionsRaw" },
      { type: "string", key: "estampesMultiplesMatriceWeight" },
      { type: "string", key: "estampesMultiplesMatriceRangement" },
      { type: "boolean", key: "estampesMultiplesMatriceNotLocated" },
      { type: "boolean", key: "estampesMultiplesMatriceDestroy" },
      { type: "boolean", key: "estampesMultiplesMatriceNotPrintable" },
      { type: "string", key: "estampesMultiplesMatriceStateNotes" },
      { type: "string", key: "estampesMultiplesMatriceNotes" },
    ],
  },
  {
    type: "group",
    label: "epreuveTirage",
    fields: [
      {
        type: "multilingual",
        key: "estampesMultiplesDescriptionDescriptionCompleteMultilingual",
      },
    ],
  },
  {
    type: "group",
    label: "multiplesampleLabelGroup",
    fields: [
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
        label: "multiplesampleDescription.advancedSearch.numeroEnregistrement",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionNumero",
        label: "multiplesampleDescription.advancedSearch.numero",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
        label: "multiplesampleDescription.advancedSearch.numeroInventaire",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionNumeroInventairePubli",
        label:
          "multiplesampleDescription.advancedSearch.numeroInventairePublished",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionNotes",
        label: "multiplesampleDescription.advancedSearch.notes",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionStatus",
        label: "multiplesampleDescription.advancedSearch.status",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionAncienNumero",
        label: "multiplesampleDescription.advancedSearch.ancienNumero",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionAncienPublished",
        label: "multiplesampleDescription.advancedSearch.ancienNumeroPublished",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionAutreNumero",
        label: "multiplesampleDescription.advancedSearch.autreNumero",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionAutrePublished",
        label: "multiplesampleDescription.advancedSearch.autreNumeroPublished",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionType",
        label: "multiplesampleDescription.advancedSearch.type",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionPrinter",
        label: "multiplesampleDescription.advancedSearch.printer",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionEditor",
        label: "multiplesampleDescription.advancedSearch.editor",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionDateTirage",
        label: "multiplesampleDescription.advancedSearch.dateTirage",
      },
      {
        type: "multilingual",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesTechnique",
        label: "multiplesampleDescription.advancedSearch.technique",
      },
      {
        type: "multilingual",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesSupport",
        label: "multiplesampleDescription.advancedSearch.support",
      },
      {
        type: "multilingual",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesEditionNumber",
        label: "multiplesampleDescription.advancedSearch.editionNumber",
      },
      {
        type: "multilingual",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesSignatureInscription",
        label: "multiplesampleDescription.advancedSearch.signatureInscription",
      },
      {
        type: "multilingual",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesInscriptionDos",
        label: "multiplesampleDescription.advancedSearch.inscriptionDos",
      },
      {
        type: "multilingual",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesInscriptionDosPublished",
        label:
          "multiplesampleDescription.advancedSearch.inscriptionDosPublished",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesEstimationDescription",
        label: "multiplesampleDescription.advancedSearch.estimationDescription",
      },
      {
        type: "currency",
        key: "relatedMultiplesample.some.exemplaireDansEstampesEstimationAmount",
        label: "multiplesampleDescription.advancedSearch.estimationAmount",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesSaleDescription",
        label: "multiplesampleDescription.advancedSearch.saleDescription",
      },
      {
        type: "currency",
        key: "relatedMultiplesample.some.exemplaireDansEstampesSaleAmount",
        label: "multiplesampleDescription.advancedSearch.saleAmount",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesAcquisitionDescription",
        label:
          "multiplesampleDescription.advancedSearch.acquisitionDescription",
      },
      {
        type: "currency",
        key: "relatedMultiplesample.some.exemplaireDansEstampesAcquisitionAmount",
        label: "multiplesampleDescription.advancedSearch.acquisitionAmount",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionLocalisation",
        label: "multiplesampleDescription.advancedSearch.localisation",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesStorageLocation2",
        label: "multiplesampleDescription.advancedSearch.storageLocation2",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesStorageLocation3",
        label: "multiplesampleDescription.advancedSearch.storageLocation3",
      },
      {
        type: "dimensions",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensions",
        label: "multiplesampleDescription.advancedSearch.dimensions",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsVerifiees",
        label: "multiplesampleDescription.advancedSearch.dimensionsVerifiees",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsInconnues",
        label: "multiplesampleDescription.advancedSearch.dimensionsInconnues",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsDouteuses",
        label: "multiplesampleDescription.advancedSearch.dimensionsDouteuses",
      },
      {
        type: "dimensions",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsImage",
        label: "multiplesampleDescription.advancedSearch.dimensionsImage",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsImageVerifiees",
        label:
          "multiplesampleDescription.advancedSearch.dimensionsImageVerifiees",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsImageInconnues",
        label:
          "multiplesampleDescription.advancedSearch.dimensionsImageInconnues",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsImageDouteuses",
        label:
          "multiplesampleDescription.advancedSearch.dimensionsImageDouteuses",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsPoids",
        label: "multiplesampleDescription.advancedSearch.dimensionsPoids",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsRaw",
        label: "multiplesampleDescription.advancedSearch.dimensionsRaw",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesDimensionsIsProfondeur",
        label:
          "multiplesampleDescription.advancedSearch.dimensionsIsProfondeur",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionNotesTechniques",
        label: "multiplesampleDescription.advancedSearch.notesTechniques",
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesDescriptionNotesConditions",
        label: "multiplesampleDescription.advancedSearch.notesConditions",
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.exemplaireDansEstampesMultiplesOeuvreNonLocalisee",
        label: "multiplesampleDescription.advancedSearch.oeuvreNonLocalisee",
      },
    ],
  },
  {
    type: "group",
    label: "bibliographyLabelExport",
    fields: [
      {
        type: "string",
        key: "multipleprintInBibliography.some.bibliographieDansEstampesMultiplesDescriptionReproduitPage",
        label: multipleprintBibliographyAdvancedSearchLabels.reproduitPage,
      },
      {
        type: "string",
        key: "multipleprintInBibliography.some.bibliographieDansEstampesMultiplesDescriptionReproduitPlanche",
        label: multipleprintBibliographyAdvancedSearchLabels.reproduitPlanche,
      },
      {
        type: "boolean",
        key: "multipleprintInBibliography.some.biblioInMultiPrintDescPlancheNonNum",
        label: multipleprintBibliographyAdvancedSearchLabels.plancheNonNumerotee,
      },
      {
        type: "string",
        key: "multipleprintInBibliography.some.bibliographieDansEstampesMultiplesDescriptionCitePage",
        label: multipleprintBibliographyAdvancedSearchLabels.citePage,
      },
      {
        type: "string",
        key: "multipleprintInBibliography.some.bibliographieDansEstampesMultiplesDescriptionListePage",
        label: multipleprintBibliographyAdvancedSearchLabels.listePage,
      },
      {
        type: "string",
        key: "multipleprintInBibliography.some.bibliographieDansEstampesMultiplesDescriptionSousNumero",
        label: multipleprintBibliographyAdvancedSearchLabels.sousNumero,
      },
      {
        type: "boolean",
        key: "multipleprintInBibliography.some.bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
        label: multipleprintBibliographyAdvancedSearchLabels.reproductionCouleur,
      },
      {
        type: "boolean",
        key: "multipleprintInBibliography.some.biblioInMultiPrintDescReproCover",
        label: multipleprintBibliographyAdvancedSearchLabels.reproductionCouverture,
      },
      {
        type: "boolean",
        key: "multipleprintInBibliography.some.bibliographieDansEstampesMultiplesDescriptionNonPagine",
        label: multipleprintBibliographyAdvancedSearchLabels.nonPagine,
      },
      {
        type: "string",
        key: "multipleprintInBibliography.some.bibliographieDansEstampesMultiplesDescriptionTexteLibre",
        label: multipleprintBibliographyAdvancedSearchLabels.texteLibre,
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiplesampleDansBibliographieDescriptionReproduitPage",
        label: multiplesampleBibliographyAdvancedSearchLabels.reproduitPage,
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiplesampleDansBibliographieDescriptionReproduitPlanche",
        label: multiplesampleBibliographyAdvancedSearchLabels.reproduitPlanche,
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiplesampleDansBibliographieDescriptionPlancheNonNumerotee",
        label: multiplesampleBibliographyAdvancedSearchLabels.plancheNonNumerotee,
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiplesampleDansBibliographieDescriptionCitePage",
        label: multiplesampleBibliographyAdvancedSearchLabels.citePage,
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiplesampleDansBibliographieDescriptionListePage",
        label: multiplesampleBibliographyAdvancedSearchLabels.listePage,
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiplesampleDansBibliographieDescriptionSousNumero",
        label: multiplesampleBibliographyAdvancedSearchLabels.sousNumero,
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiplesampleDansBibliographieDescriptionReproductionCouleur",
        label: multiplesampleBibliographyAdvancedSearchLabels.reproductionCouleur,
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiSampleInBiblioDescReproCover",
        label: multiplesampleBibliographyAdvancedSearchLabels.reproductionCouverture,
      },
      {
        type: "boolean",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiplesampleDansBibliographieDescriptionNonPagine",
        label: multiplesampleBibliographyAdvancedSearchLabels.nonPagine,
      },
      {
        type: "string",
        key: "relatedMultiplesample.some.multipleSampleInBibliography.some.multiplesampleDansBibliographieDescriptionTexteLibre",
        label: multiplesampleBibliographyAdvancedSearchLabels.texteLibre,
      },
    ],
  },
  {
    type: "group",
    label: "sources",
    fields: [{ type: "string", key: "sources" }],
  },
  artistLastNameSortGroup(),
  attachmentPhotoAdvancedSearchGroup,
];
